/**
 * Partes do nome do paciente para os botões de cópia do cabeçalho da ficha.
 *
 * A recepção cola esses pedaços em sistemas de convênio e mensagens: às vezes
 * o campo pede só o primeiro nome, às vezes só o sobrenome completo (com as
 * partículas "da/de/dos" que fazem parte dele).
 */

/** Primeira palavra do nome ("Maria" em "Maria da Silva Souza"). */
export function firstNameOf(name: string | undefined | null): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  return parts[0] ?? '';
}

/** Agnomes que não identificam ninguém sozinhos — "João Junior" não diz qual
 *  João; vão junto com o sobrenome de antes ("João Silva Junior"). */
const NAME_SUFFIXES = new Set([
  'JUNIOR', 'JÚNIOR', 'JR', 'JR.', 'FILHO', 'FILHA', 'NETO', 'NETA',
  'SOBRINHO', 'SOBRINHA', 'SEGUNDO', 'TERCEIRO',
]);

/** Partículas que somem quando o nome do meio vira inicial. */
const NAME_PARTICLES = new Set(['DE', 'DA', 'DO', 'DAS', 'DOS', 'E']);
const isParticle = (p: string) => NAME_PARTICLES.has(p.toUpperCase());

/**
 * Quantas palavras do começo formam os DOIS PRIMEIROS NOMES, que nenhuma
 * forma curta abrevia ou tira (pedido de 26/10/2026: "os 02 primeiros nomes
 * sempre exibidos por completo"). Partícula logo depois do primeiro vai junto
 * com o nome seguinte ("Maria de Fátima"), como na voz (`spokenNameOf`).
 */
function firstTwoCount(parts: string[]): number {
  return Math.min(parts.length, parts.length > 1 && isParticle(parts[1]) ? 3 : 2);
}

/**
 * Nome CURTO pra espaço apertado (coluna da visão semana): os dois primeiros
 * nomes + o último sobrenome — "Maria Alzenira dos Santos Ferreira" → "Maria
 * Alzenira Ferreira". Agnome fica com o sobrenome de antes ("João Pedro Silva
 * Junior"). Sem nada entre os dois primeiros e o último, volta como está.
 */
export function shortNameOf(name: string | undefined | null): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  const head = firstTwoCount(parts);
  if (parts.length <= head + 1) return parts.join(' ');
  const last = parts[parts.length - 1];
  const tail = NAME_SUFFIXES.has(last.toUpperCase()) ? [parts[parts.length - 2], last] : [last];
  return [...parts.slice(0, head), ...tail].join(' ');
}

/**
 * Nome MÍNIMO pra coluna estreitíssima: só os dois primeiros nomes, inteiros —
 * "Maria Alzenira dos Santos Ferreira" → "Maria Alzenira", "Maria de Fátima
 * Pires" → "Maria de Fátima". Menos que isso não identifica o paciente.
 */
export function tinyNameOf(name: string | undefined | null): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  return parts.slice(0, firstTwoCount(parts)).join(' ');
}

/**
 * Sobrenome COMPLETO — tudo depois do primeiro nome, preservando partículas
 * ("da Silva Souza"). Vazio quando o cadastro só tem uma palavra.
 */
export function lastNameOf(name: string | undefined | null): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  return parts.length > 1 ? parts.slice(1).join(' ') : '';
}

/**
 * Nome pra voz do sistema CHAMAR o paciente (pedido de 23/10/2026): os dois
 * primeiros nomes — "Francineide Vieira" —, ou três quando o segundo é
 * partícula ("Maria de Fátima", "Francisco dos Santos"). Caixa normal com a
 * partícula minúscula: o cadastro vem em CAIXA ALTA e a voz do Windows
 * soletra palavra curta em maiúsculas ("DE" → "dê é").
 */
export function spokenNameOf(name: string | undefined | null): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  const picked = parts.slice(0, firstTwoCount(parts));
  // Partícula no fim ("Maria de", cadastro cortado) não é nome: sai.
  while (picked.length > 1 && isParticle(picked[picked.length - 1])) picked.pop();
  return picked
    .map((p, i) => {
      const lower = p.toLocaleLowerCase('pt-BR');
      return i > 0 && isParticle(p)
        ? lower
        : lower.charAt(0).toLocaleUpperCase('pt-BR') + lower.slice(1);
    })
    .join(' ');
}

/**
 * Nome com os do MEIO abreviados, pro cabeçalho da ficha quando o nome
 * inteiro não cabe: "Leopoldo Gomes Pinheiro Neto" → "Leopoldo Gomes P.
 * Neto". Os dois primeiros nomes e o último ficam inteiros (são os que
 * identificam); partículas ("de", "dos"…) do meio somem. Sem nada entre os
 * dois primeiros e o último, volta como está. Melhor que cortar no meio
 * ("Leo…").
 */
export function abbreviateMiddleNames(name: string | undefined | null): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  const head = firstTwoCount(parts);
  if (parts.length <= head + 1) return parts.join(' ');
  const middle = parts
    .slice(head, -1)
    .filter((p) => !isParticle(p))
    .map((p) => `${p.charAt(0).toUpperCase()}.`);
  return [...parts.slice(0, head), ...middle, parts[parts.length - 1]].join(' ');
}
